import { editorClient } from "@/lib/editorClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import z from "zod";
import { Trans } from "@lingui/react/macro";
import { redirect, notFound } from "@tanstack/react-router";
import { languageKeys, languageMap } from "@op/puck-types";
import { LegalForm } from "@/components/app/language-settings/LegalForm";
import { OverviewPageForm } from "@/components/app/language-settings/OverviewPageForm";

async function getOverviewPageSettingsData({
  organizationSlug,
  projectSlug,
  languageKey,
}: ParamsSchema) {
  const res = await editorClient.languages[":organizationSlug"][":projectSlug"][
    ":languageKey"
  ].settings["overview-page"].$get({
    param: {
      organizationSlug,
      projectSlug,
      languageKey,
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw redirect({ to: "/login" });
    if (res.status === 404) throw notFound();
    throw new Error("Failed to get overview page settings");
  }
  const overviewPageSettings = await res.json();
  return overviewPageSettings;
}

async function getLegalSettingsData({
  organizationSlug,
  projectSlug,
  languageKey,
}: ParamsSchema) {
  const res = await editorClient.languages[":organizationSlug"][":projectSlug"][
    ":languageKey"
  ].settings.legal.$get({
    param: {
      organizationSlug,
      projectSlug,
      languageKey,
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw redirect({ to: "/login" });
    if (res.status === 404) throw notFound();
    throw new Error("Failed to get domain settings");
  }
  const domain = await res.json();
  return domain;
}

const overviewPageSettingsQueryKey = (params: ParamsSchema) => [
  "editor-language-settings-overview-page",
  params,
];

const legalSettingsQueryKey = (params: ParamsSchema) => [
  "editor-language-settings-legal",
  params,
];

const paramsSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
  languageKey: z.enum(languageKeys),
});

type ParamsSchema = z.infer<typeof paramsSchema>;

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/settings",
)({
  component: RouteComponent,
  params: paramsSchema,
  loader: async ({ params, context: { queryClient } }) => {
    const overviewPageSettings = await getOverviewPageSettingsData(params);
    queryClient.setQueryData(
      overviewPageSettingsQueryKey(params),
      overviewPageSettings,
    );
    const legalSettings = await getLegalSettingsData(params);
    queryClient.setQueryData(legalSettingsQueryKey(params), legalSettings);
  },
});

function RouteComponent() {
  const params = Route.useParams();
  const queryClient = useQueryClient();
  const { organizationSlug, projectSlug, languageKey } = params;

  const { data: legalSettings } = useQuery(
    {
      queryKey: legalSettingsQueryKey(params),
      queryFn: () => getLegalSettingsData(params),
    },
    queryClient,
  );

  const { data: overviewPageSettings } = useQuery(
    {
      queryKey: overviewPageSettingsQueryKey(params),
      queryFn: () => getOverviewPageSettingsData(params),
    },
    queryClient,
  );

  return (
    <div className="container max-w-none flex w-full flex-col items-center gap-6 bg-default-background py-12 overflow-auto">
      <div className="flex w-full max-w-[576px] flex-col items-start gap-6">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="w-full text-heading-2 font-heading-2 text-default-font">
            <Trans>Language settings</Trans>
          </span>
          <span className="text-body font-body text-subtext-color">
            <Trans>
              Language settings for {projectSlug} - {languageMap[languageKey]}
            </Trans>
          </span>
        </div>
        <OverviewPageForm
          overviewPageSettings={overviewPageSettings ?? null}
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
          languageKey={languageKey}
        />
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
        <LegalForm
          key={legalSettings ? "ready" : "pending"}
          legalSettings={legalSettings ?? null}
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
          languageKey={languageKey}
        />
      </div>
    </div>
  );
}
